/**
 * REVERSE CHARGE.
 *
 * Its own screen rather than a seventh block on Statutory & Deductions,
 * because it is the only screen in this module where somebody DOES something
 * rather than reads a figure: a self-invoice is raised here, a payment voucher
 * is raised here, and a tax position is settled here. Statutory is a report.
 *
 * THE FOUR QUESTIONS IT ANSWERS, IN THE ORDER THEY MATTER
 * ------------------------------------------------------
 *   1. WHAT IS THE CLOCK ON. Rule 47A gives thirty days from the date the
 *      supply was received to raise the self-invoice. That is the only thing
 *      on this screen with a deadline, so it is first and it is loud.
 *   2. WHAT HAS BEEN ISSUED. The register, with the month totals that go into
 *      GSTR-3B table 3.1(d) and the credit claimed against them in 4(A)(3).
 *   3. WHAT THE TWO RULES SAY, side by side. Until the switch is flipped this
 *      is the whole point of the screen: what today's rule answers, what the
 *      notified list answers, the entry cited, and the difference in rupees.
 *   4. WHAT NOBODY HAS DECIDED. An F-PAN on a non-body-corporate entry is a
 *      firm or an LLP and the two go opposite ways. Listed separately, because
 *      a line the system is guessing at is worse than one it refuses.
 *
 * WHY THE SWITCH IS ON THIS SCREEN AND ADMIN-ONLY
 * ----------------------------------------------
 * It moves a tax figure on every future bill. It sits under the comparison it
 * is meant to be read against, so that flipping it is the last thing somebody
 * does after looking at the evidence rather than the first thing they find.
 * The database refuses the write to anyone but an admin; the control is hidden
 * from everyone else rather than shown and then refused.
 *
 * WHAT IS NOT HERE
 * ----------------
 * An advocate's fee, a GTA's freight and rent from an unregistered landlord
 * are the section 9(3) entries OVAL meets most often, and every one of them
 * arrives through Material Procurement. The two modules are independent by
 * board instruction of 19-09-2026, so this screen covers the works side only
 * and says so on its face rather than letting somebody believe the register is
 * complete.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, CheckCircle2, Clock, Download, FileWarning, Landmark,
  Receipt, Scale, ShieldQuestion,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import {
  Alert, Card, Confirm, EmptyState, Loading, Modal, Select, Stat, Textarea,
  useToast,
} from '@/components/ui'

const n = (v: unknown) => Number(v ?? 0) || 0

interface DueRow {
  ra_bill_id: string; ra_no: number; wo_id: string; wo_title: string | null
  site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null; is_gst_registered: boolean
  bill_date: string; period_to: string | null; approval_date: string | null
  gross_value: number; rcm_gst_amount: number
  supply_received_on: string; due_by: string; days_left: number; overdue: boolean
}

interface SelfInvoiceRow {
  id: string; ra_bill_id: string | null; ra_no: number | null
  wo_id: string | null; wo_title: string | null
  supplier_name: string; supplier_pan: string | null
  site_name: string | null
  supply_received_on: string; invoice_date: string; due_by: string
  days_taken: number | null; issued_late: boolean
  sac_code: string | null; taxable_value: number; gst_rate_percent: number
  cgst_amount: number; sgst_amount: number; igst_amount: number; total_tax: number
  place_of_supply: string
  rcm_notification: string | null; rcm_entry_no: string | null
  status: string; month_label: string; month_key: string; fy_label: string
  voucher_count: number; vouchered_amount: number
  cancelled_reason: string | null
}

interface VoucherRow {
  id: string; self_invoice_id: string | null; ra_bill_id: string | null
  ra_no: number | null; supplier_name: string; site_name: string | null
  voucher_date: string; amount_paid: number; gst_rate_percent: number
  total_tax: number; mode: string | null; reference_no: string | null
  status: string
}

interface VoucherDueRow {
  payment_id: string; ra_bill_id: string; ra_no: number
  wo_id: string; wo_title: string | null
  vendor_id: string; vendor_name: string | null; is_gst_registered: boolean
  site_id: string; site_name: string | null
  payment_date: string; amount: number; mode: string | null; reference_no: string | null
  gross_value: number; rcm_gst_amount: number
  tax_rate: number; tax_on_this_payment: number
  self_invoice_id: string | null; self_invoice_missing: boolean
}

interface DetRow {
  wo_id: string; wo_title: string | null; wo_status: string
  site_name: string | null; vendor_name: string | null
  is_gst_registered: boolean; vendor_pan: string | null
  vendor_entity_class: string
  wo_line_id: string; sl_no: number; line_description: string | null
  sac_code: string | null; sac_description: string | null
  line_amount: number; line_gst_rate: number
  verdict_today: string; tax_today: number; tax_today_who: string
  rcm_override: string | null
  verdict_notified: string; verdict_from_list: string
  rate_notified: number; authority: string; reason: string
  tax_notified: number; difference: number
  needs_a_decision: boolean
}

interface SettingRow { key: string; value: string; description: string }

const VERDICT_TONE: Record<string, string> = {
  RCM: 'bg-amber-50 text-amber-800 ring-amber-200',
  FORWARD: 'bg-slate-50 text-slate-700 ring-slate-200',
  NO_TAX: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  AMBIGUOUS: 'bg-rose-50 text-rose-800 ring-rose-200',
}
const VERDICT_WORDS: Record<string, string> = {
  RCM: 'OVAL pays the tax',
  FORWARD: 'he charges the tax',
  NO_TAX: 'no tax arises',
  AMBIGUOUS: 'needs a decision',
}

function Verdict({ v }: { v: string }) {
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ${
      VERDICT_TONE[v] ?? VERDICT_TONE.FORWARD}`}>
      {VERDICT_WORDS[v] ?? v}
    </span>
  )
}

export function ReverseCharge() {
  const toast = useToast()
  const { profile } = useAuth()
  const isAdmin = profile?.role === 'admin'
  const mayBook = ['admin', 'accounts_spoc', 'procurement_head', 'procurement_manager']
    .includes(profile?.role ?? '')

  const [busy, setBusy] = useState(false)
  const [raising, setRaising] = useState<DueRow | null>(null)
  const [receivedOn, setReceivedOn] = useState('')
  const [remarks, setRemarks] = useState('')
  const [cancelling, setCancelling] = useState<SelfInvoiceRow | null>(null)
  const [cancellingVoucher, setCancellingVoucher] = useState<VoucherRow | null>(null)
  const [overriding, setOverriding] = useState<DetRow | null>(null)
  const [overrideTo, setOverrideTo] = useState('FORCE_FORWARD')
  const [overrideWhy, setOverrideWhy] = useState('')
  const [onlyDifferences, setOnlyDifferences] = useState(true)

  const { data: settings, refresh: refreshSettings } = useQuery<SettingRow>(
    'svc_settings',
    () => supabase.from('service_settings').select('*'),
    [], { cacheable: false, idField: 'key' })

  const { data: due, loading, refresh: refreshDue } = useQuery<DueRow>(
    'rcm_due',
    () => supabase.from('v_rcm_due').select('*').order('due_by'),
    [], { cacheable: false, idField: 'ra_bill_id' })

  const { data: issued, refresh: refreshIssued } = useQuery<SelfInvoiceRow>(
    'rcm_self_invoices',
    () => supabase.from('v_rcm_self_invoices').select('*').order('invoice_date', { ascending: false }),
    [], { cacheable: false })

  const { data: vouchers, refresh: refreshVouchers } = useQuery<VoucherRow>(
    'rcm_vouchers',
    () => supabase.from('v_rcm_payment_vouchers').select('*').order('voucher_date', { ascending: false }),
    [], { cacheable: false })

  const { data: voucherDue, refresh: refreshVoucherDue } = useQuery<VoucherDueRow>(
    'rcm_voucher_due',
    () => supabase.from('v_rcm_voucher_due').select('*').order('payment_date'),
    [], { cacheable: false, idField: 'payment_id' })

  const { data: determination, refresh: refreshDet } = useQuery<DetRow>(
    'rcm_determination',
    () => supabase.from('v_rcm_determination').select('*').order('wo_id'),
    [], { cacheable: false, idField: 'wo_line_id' })

  const byCategory = settings.find((s) => s.key === 'rcm_by_notified_category')?.value === 'true'

  const refreshAll = () => {
    refreshDue(); refreshIssued(); refreshVouchers(); refreshVoucherDue(); refreshDet()
  }

  // ---- the figures ---------------------------------------------------------
  const live = issued.filter((s) => s.status === 'ISSUED')
  const overdue = due.filter((d) => d.overdue)
  const taxDeclared = live.reduce((a, s) => a + n(s.total_tax), 0)
  const taxAwaiting = due.reduce((a, d) => a + n(d.rcm_gst_amount), 0)
  const needDecision = determination.filter((d) => d.needs_a_decision)

  const months = useMemo(() => {
    const m = new Map<string, { label: string; taxable: number; tax: number; count: number; late: number }>()
    for (const s of live) {
      const e = m.get(s.month_key) ?? { label: s.month_label, taxable: 0, tax: 0, count: 0, late: 0 }
      e.taxable += n(s.taxable_value); e.tax += n(s.total_tax); e.count += 1
      if (s.issued_late) e.late += 1
      m.set(s.month_key, e)
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [live])

  /** One row per work order, not per line — the comparison reads as an order. */
  const comparison = useMemo(() => {
    const m = new Map<string, {
      wo_id: string; title: string | null; site: string | null; vendor: string | null
      registered: boolean; entity: string; today: number; notified: number
      verdicts: Set<string>; override: string | null; authority: string
      reason: string; lines: number; ambiguous: boolean
    }>()
    for (const d of determination) {
      const e = m.get(d.wo_id) ?? {
        wo_id: d.wo_id, title: d.wo_title, site: d.site_name, vendor: d.vendor_name,
        registered: d.is_gst_registered, entity: d.vendor_entity_class,
        today: 0, notified: 0, verdicts: new Set<string>(), override: d.rcm_override,
        authority: d.authority, reason: d.reason, lines: 0, ambiguous: false,
      }
      e.today += n(d.tax_today)
      e.notified += n(d.tax_notified)
      e.verdicts.add(d.verdict_from_list)
      e.lines += 1
      if (d.needs_a_decision) e.ambiguous = true
      m.set(d.wo_id, e)
    }
    /**
     * A ROW MATTERS IF THE PAYER CHANGES, NOT ONLY IF THE RUPEES DO.
     *
     * Reported 19-09-2026 on WO-TR7-20260919-005 — a registered security
     * agency under entry 14. Today's rule says he charges 9,720. The notified
     * list says OVAL owes the same 9,720 directly. The amount is identical,
     * the difference is zero, and until this was fixed the row was FILTERED
     * OUT of the comparison by default.
     *
     * That is precisely backwards. A change of payer is the more serious of
     * the two: an amount you already expected to pay moving between columns
     * is a reconciliation, but tax you did not know you owed is a liability,
     * a return line and, if missed, interest. Hiding it because the
     * arithmetic matched would have hidden the single most important row on
     * the screen from the person the screen was built for.
     */
    const rows = [...m.values()].map((e) => {
      const todayVerdict = e.registered ? 'FORWARD' : 'RCM'
      const payerChanged = !(e.verdicts.size === 1 && e.verdicts.has(todayVerdict))
      return {
        ...e,
        difference: Math.round((e.notified - e.today) * 100) / 100,
        todayVerdict,
        payerChanged,
      }
    })
    return rows
  }, [determination])

  /**
   * An order where the two rules agree is hidden by default, and the FACT
   * that it is hidden has to be said out loud.
   *
   * Reported 19-09-2026: a new work order was raised against a REGISTERED
   * contractor for ordinary labour, and it did not appear here. Both rules
   * answered "he charges the tax" -- correct, agreed, nothing to decide -- so
   * the filter dropped it. But from the outside "my order is missing" and "my
   * order is fine" look identical, and only one of them is worth a phone call.
   * So the count of what is hidden is printed under the table.
   */
  const shownComparison = useMemo(() => (
    onlyDifferences
      ? comparison.filter((r) => Math.abs(r.difference) > 0.005 || r.ambiguous || r.payerChanged)
      : comparison
  ), [comparison, onlyDifferences])

  const agreeing = comparison.length - shownComparison.length
  const wouldChange = shownComparison.reduce((a, r) => a + r.difference, 0)
  // Orders where the money does not move but the obligation does. Counted
  // separately because a net of zero across the page would otherwise read as
  // "nothing to do" on exactly the orders that create a new liability.
  const payerFlips = shownComparison.filter((r) => r.payerChanged && r.difference === 0)
  const newLiability = payerFlips
    .filter((r) => !r.verdicts.has('FORWARD'))
    .reduce((a, r) => a + r.notified, 0)

  // ---- actions -------------------------------------------------------------
  async function raiseSelfInvoice() {
    if (!raising) return
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_rcm_raise_self_invoice', {
        p_ra_id: raising.ra_bill_id,
        p_supply_received_on: receivedOn || null,
        p_remarks: remarks || null,
      })
      if (error) throw error
      toast.success('Self-invoice raised')
      setRaising(null); setReceivedOn(''); setRemarks('')
      refreshAll()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not raise it'))
    } finally { setBusy(false) }
  }

  async function cancelSelfInvoice(reason: string) {
    if (!cancelling) return
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_rcm_cancel_self_invoice', {
        p_id: cancelling.id, p_reason: reason,
      })
      if (error) throw error
      toast.success('Cancelled')
      setCancelling(null); refreshAll()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not cancel it'))
    } finally { setBusy(false) }
  }

  /**
   * Raising a voucher takes no dialogue: the payment fixes the date, the
   * amount, the mode and the reference, and the tax is its share of the
   * bill. There is nothing for a human to decide, so asking would be
   * ceremony. Cancelling DOES ask, because a reason goes on the record.
   */
  async function raiseVoucher(paymentId: string) {
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_rcm_raise_payment_voucher', {
        p_payment_id: paymentId, p_remarks: null,
      })
      if (error) throw error
      toast.success('Payment voucher raised')
      refreshAll()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not raise the voucher'))
    } finally { setBusy(false) }
  }

  async function cancelVoucher(reason: string) {
    if (!cancellingVoucher) return
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_rcm_cancel_payment_voucher', {
        p_id: cancellingVoucher.id, p_reason: reason,
      })
      if (error) throw error
      toast.success('Cancelled')
      setCancellingVoucher(null); refreshAll()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not cancel it'))
    } finally { setBusy(false) }
  }

  async function saveOverride() {
    if (!overriding) return
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_wo_set_rcm_override', {
        p_wo_id: overriding.wo_id,
        p_override: overrideTo || null,
        p_reason: overrideWhy,
      })
      if (error) throw error
      toast.success('Recorded')
      setOverriding(null); setOverrideWhy(''); refreshDet()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not record it'))
    } finally { setBusy(false) }
  }

  async function flipSwitch(to: boolean) {
    setBusy(true)
    try {
      const { error } = await supabase.from('service_settings')
        .update({ value: String(to), updated_at: new Date().toISOString() })
        .eq('key', 'rcm_by_notified_category')
      if (error) throw error
      toast.success(to ? 'The notified list now decides' : 'Back to the earlier rule')
      refreshSettings(); refreshAll()
    } catch (e) {
      toast.error(dbMessage(e, 'Could not change it'))
    } finally { setBusy(false) }
  }

  async function download(kind: 'self_invoice_id' | 'voucher_id', id: string) {
    setBusy(true)
    try {
      await downloadFromFunction('generate-rcm-pdf', { [kind]: id }, `${id}.pdf`)
    } catch (e) {
      toast.error(dbMessage(e, 'Could not produce the PDF'))
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the reverse-charge position…" />

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Reverse charge</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Tax OVAL pays to the Government directly on a supply it has received, and the
          two documents the law requires when it does. Works contracts only — a lawyer's
          fee, a transporter's freight or rent from an unregistered landlord come through
          Material Procurement and are not on this register.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Awaiting a self-invoice" value={rupees(taxAwaiting)}
              sub={`${due.length} bill${due.length === 1 ? '' : 's'}`}
              tone={due.length ? 'warn' : 'default'} icon={<Clock className="h-4 w-4" />} />
        <Stat label="Past the thirty days" value={String(overdue.length)}
              sub="rule 47A" tone={overdue.length ? 'bad' : 'good'}
              icon={<AlertTriangle className="h-4 w-4" />} />
        <Stat label="Declared on self-invoices" value={rupees(taxDeclared)}
              sub={`${live.length} issued`} icon={<Receipt className="h-4 w-4" />} />
        <Stat label="Needing a decision" value={String(needDecision.length)}
              sub="supplier status unclear"
              tone={needDecision.length ? 'warn' : 'default'}
              icon={<ShieldQuestion className="h-4 w-4" />} />
      </div>

      {!byCategory && (
        <Alert tone="warning" title="Reverse charge is still decided by the old rule">
          Every unregistered contractor is being treated as reverse charge, whatever the
          service. Section 9(4) reaches only a promoter of a real estate project, which
          OVAL is not — so on the notified list most of these bills carry no tax at all.
          The comparison below shows both answers. Nothing changes until an administrator
          turns the notified list on.
        </Alert>
      )}

      {/* 1 ------------------------------------------------------------------ */}
      <Card title="On the clock"
            subtitle="Approved bills from unregistered suppliers carrying reverse-charge tax, with no self-invoice against them. Rule 47A allows thirty days from the date the supply was received.">
        {due.length === 0 ? (
          <EmptyState icon={<CheckCircle2 className="h-8 w-8" />}
                      title="Nothing waiting"
                      message="Every reverse-charge bill has its self-invoice." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Bill</th><th>Contractor</th><th>Supply received</th>
                  <th>Due by</th><th className="num">Taxable</th>
                  <th className="num">Tax</th><th />
                </tr>
              </thead>
              <tbody>
                {due.map((d) => (
                  <tr key={d.ra_bill_id} className={d.overdue ? 'bg-rose-50/60' : undefined}>
                    <td className="whitespace-nowrap">
                      <Link className="font-medium text-emerald-700 hover:underline"
                            to={`/service/ra-bills/${d.ra_bill_id}`}>
                        RA-{d.ra_no}
                      </Link>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {d.wo_id} · {d.site_name}
                      </span>
                    </td>
                    <td className="max-w-[180px] truncate">{d.vendor_name}</td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(d.supply_received_on)}</td>
                    <td className="whitespace-nowrap text-xs">
                      {ddmmyyyy(d.due_by)}
                      <span className={`block text-[11px] font-semibold ${
                        d.overdue ? 'text-rose-700' : 'text-slate-400'}`}>
                        {d.overdue
                          ? `${Math.abs(d.days_left)} days late`
                          : `${d.days_left} days left`}
                      </span>
                    </td>
                    <td className="num">{rupees(d.gross_value)}</td>
                    <td className="num font-semibold text-amber-700">{rupees(d.rcm_gst_amount)}</td>
                    <td className="text-right">
                      {mayBook && (
                        <button className="btn-primary btn-sm" disabled={busy}
                                onClick={() => {
                                  setRaising(d)
                                  setReceivedOn(d.supply_received_on)
                                  setRemarks('')
                                }}>
                          Raise self-invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 2 ------------------------------------------------------------------ */}
      <Card title="Self-invoices issued"
            subtitle="Section 31(3)(f). The liability declared in GSTR-3B table 3.1(d), and the credit claimed against it in 4(A)(3).">
        {issued.length === 0 ? (
          <EmptyState icon={<Receipt className="h-8 w-8" />} title="None yet"
                      message="A self-invoice appears here as soon as one is raised above." />
        ) : (
          <>
            {months.length > 0 && (
              <div className="mb-5">
                {/*
                  Reported 19-09-2026: Accounts went looking for "the return
                  figure" and did not recognise it, because this table sat
                  under the card's own subtitle with nothing of its own to say
                  what it was. Two tables in one card need two headings; the
                  card title can only name one of them.
                */}
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  By month — what goes on the return
                </p>
                <div className="overflow-x-auto">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Month</th><th className="num">Invoices</th>
                      <th className="num">Taxable value</th>
                      <th className="num">
                        Tax payable
                        <span className="block text-[10px] font-normal normal-case text-slate-400">
                          GSTR-3B 3.1(d)
                        </span>
                      </th>
                      <th className="num">
                        Credit claimable
                        <span className="block text-[10px] font-normal normal-case text-slate-400">
                          GSTR-3B 4(A)(3)
                        </span>
                      </th>
                      <th className="num">Late</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map(([key, m]) => (
                      <tr key={key}>
                        <td className="font-medium">{m.label}</td>
                        <td className="num text-xs">{m.count}</td>
                        <td className="num">{rupees(m.taxable)}</td>
                        <td className="num font-semibold text-amber-700">{rupees(m.tax)}</td>
                        <td className="num text-emerald-700">{rupees(m.tax)}</td>
                        <td className={`num text-xs ${m.late ? 'font-semibold text-rose-700' : 'text-slate-400'}`}>
                          {m.late || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Document by document
            </p>
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Self-invoice</th><th>Supplier</th><th>Against</th>
                    <th>Entry</th><th className="num">Taxable</th>
                    <th className="num">CGST</th><th className="num">SGST</th>
                    <th className="num">IGST</th><th className="num">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {issued.map((s) => (
                    <tr key={s.id} className={s.status === 'CANCELLED' ? 'opacity-50' : undefined}>
                      <td className="whitespace-nowrap">
                        <span className="font-medium">{s.id}</span>
                        <span className="block text-[11px] text-slate-400">
                          {ddmmyyyy(s.invoice_date)}
                          {s.issued_late && (
                            <span className="ml-1 font-semibold text-rose-700">
                              late by {Math.max(0, (s.days_taken ?? 0) - 30)} days
                            </span>
                          )}
                          {s.status === 'CANCELLED' && (
                            <span className="ml-1 font-semibold text-rose-700">cancelled</span>
                          )}
                        </span>
                      </td>
                      <td className="max-w-[160px] truncate">{s.supplier_name}</td>
                      <td className="whitespace-nowrap text-xs">
                        {s.ra_no ? `RA-${s.ra_no}` : '—'}
                        <span className="block text-[11px] text-slate-400">{s.wo_id}</span>
                      </td>
                      <td className="whitespace-nowrap text-[11px] text-slate-500">
                        {s.rcm_notification ? `${s.rcm_notification} · ${s.rcm_entry_no}` : '—'}
                      </td>
                      <td className="num">{rupees(s.taxable_value)}</td>
                      <td className="num text-xs">{rupees(s.cgst_amount)}</td>
                      <td className="num text-xs">{rupees(s.sgst_amount)}</td>
                      <td className="num text-xs">{rupees(s.igst_amount)}</td>
                      <td className="num font-semibold text-amber-700">{rupees(s.total_tax)}</td>
                      <td className="whitespace-nowrap text-right">
                        <button className="btn-secondary btn-sm" disabled={busy}
                                onClick={() => download('self_invoice_id', s.id)}>
                          <Download className="mr-1 inline h-3 w-3" />PDF
                        </button>
                        {mayBook && s.status === 'ISSUED' && (
                          <button className="btn-ghost btn-sm ml-1 text-red-600" disabled={busy}
                                  onClick={() => setCancelling(s)}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* 2b ----------------------------------------------------------------- */}
      <Card title="Payments awaiting a voucher"
            subtitle="Section 31(3)(g), rule 52. A payment voucher is owed on every payment made on a reverse-charge supply — including to a REGISTERED supplier, who issues his own invoice but is still owed this.">
        {voucherDue.length === 0 ? (
          <EmptyState icon={<CheckCircle2 className="h-8 w-8" />}
                      title="Nothing waiting"
                      message="Every reverse-charge payment has its voucher." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Payment</th><th>Supplier</th><th>Against</th>
                  <th className="num">Paid</th><th className="num">Tax on it</th>
                  <th>Reference</th><th />
                </tr>
              </thead>
              <tbody>
                {voucherDue.map((d) => (
                  <tr key={d.payment_id}>
                    <td className="whitespace-nowrap">
                      <span className="font-medium">{d.payment_id}</span>
                      <span className="block text-[11px] text-slate-400">
                        {ddmmyyyy(d.payment_date)}
                      </span>
                    </td>
                    <td className="max-w-[160px] truncate">
                      {d.vendor_name}
                      <span className="block text-[11px] text-slate-400">
                        {d.is_gst_registered ? 'registered' : 'not registered'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      RA-{d.ra_no}
                      <span className="block text-[11px] text-slate-400">{d.wo_id}</span>
                    </td>
                    <td className="num">{rupees(d.amount)}</td>
                    <td className="num text-amber-700">
                      {rupees(d.tax_on_this_payment)}
                      <span className="block text-[11px] font-normal text-slate-400">
                        at {Number(d.tax_rate ?? 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="text-xs text-slate-500">
                      {d.mode ?? '—'}{d.reference_no ? ` · ${d.reference_no}` : ''}
                      {d.self_invoice_missing && (
                        <span className="block text-[11px] font-semibold text-amber-700">
                          no self-invoice yet
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      {mayBook && (
                        <button className="btn-primary btn-sm" disabled={busy}
                                onClick={() => raiseVoucher(d.payment_id)}>
                          Raise voucher
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Payment vouchers"
            subtitle="Issued when the supplier is paid. The tax shown is the part borne on that payment and is NOT deducted from it — it goes to the Government, not out of his money.">
        {vouchers.length === 0 ? (
          <EmptyState icon={<Landmark className="h-8 w-8" />} title="None yet"
                      message="A voucher appears here as soon as one is raised above." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Voucher</th><th>Supplier</th><th>Against</th>
                  <th className="num">Paid</th><th className="num">Tax on it</th>
                  <th>Reference</th><th />
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id} className={v.status === 'CANCELLED' ? 'opacity-50' : undefined}>
                    <td className="whitespace-nowrap">
                      <span className="font-medium">{v.id}</span>
                      <span className="block text-[11px] text-slate-400">{ddmmyyyy(v.voucher_date)}</span>
                    </td>
                    <td className="max-w-[160px] truncate">{v.supplier_name}</td>
                    <td className="text-xs">{v.ra_no ? `RA-${v.ra_no}` : '—'}</td>
                    <td className="num">{rupees(v.amount_paid)}</td>
                    <td className="num text-amber-700">{rupees(v.total_tax)}</td>
                    <td className="text-xs text-slate-500">
                      {v.mode ?? '—'}{v.reference_no ? ` · ${v.reference_no}` : ''}
                    </td>
                    <td className="text-right">
                      <button className="btn-secondary btn-sm" disabled={busy}
                              onClick={() => download('voucher_id', v.id)}>
                        <Download className="mr-1 inline h-3 w-3" />PDF
                      </button>
                      {mayBook && v.status === 'ISSUED' && (
                        <button className="btn-ghost btn-sm ml-1 text-red-600" disabled={busy}
                                onClick={() => setCancellingVoucher(v)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 3 ------------------------------------------------------------------ */}
      <Card title="What the two rules say"
            subtitle="The rule in force today against the notified section 9(3) list. Read this before the switch is moved."
            actions={
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                <input type="checkbox" checked={onlyDifferences}
                       onChange={(e) => setOnlyDifferences(e.target.checked)} />
                only where they differ
              </label>
            }>
        {shownComparison.length === 0 ? (
          <EmptyState icon={<CheckCircle2 className="h-8 w-8" />}
                      title="The two rules agree on every order"
                      message={agreeing === 1
                        ? 'One work order, and both rules give it the same answer. Nothing would move if the notified list were switched on.'
                        : `All ${agreeing} work orders get the same answer either way. Nothing would move if the notified list were switched on.`} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th><th>Contractor</th>
                  <th>Today</th><th>On the notified list</th>
                  <th className="num">Tax today</th><th className="num">Tax then</th>
                  <th className="num">Difference</th><th />
                </tr>
              </thead>
              <tbody>
                {shownComparison.map((r) => (
                  <tr key={r.wo_id} className={r.ambiguous ? 'bg-rose-50/50' : undefined}>
                    <td className="whitespace-nowrap">
                      <Link className="font-medium text-emerald-700 hover:underline"
                            to={`/service/work-orders/${r.wo_id}`}>{r.wo_id}</Link>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {r.title}
                      </span>
                    </td>
                    <td className="max-w-[160px] truncate">
                      {r.vendor}
                      <span className="block text-[11px] text-slate-400">
                        {r.registered ? 'registered' : 'not registered'}
                        {r.entity === 'FIRM_OR_LLP' && ' · firm or LLP'}
                      </span>
                    </td>
                    <td><Verdict v={r.registered ? 'FORWARD' : 'RCM'} /></td>
                    <td>
                      {[...r.verdicts].map((v) => <Verdict key={v} v={v} />)}
                      {r.override && (
                        <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          overridden
                        </span>
                      )}
                      <span className="mt-1 block max-w-[260px] text-[11px] text-slate-400">
                        {r.authority}
                      </span>
                    </td>
                    <td className="num">{rupees(r.today)}</td>
                    <td className="num">{rupees(r.notified)}</td>
                    <td className={`num font-semibold ${
                      r.difference < 0 ? 'text-emerald-700' : r.difference > 0 ? 'text-amber-700' : ''}`}>
                      {r.difference !== 0 ? rupees(r.difference) : r.payerChanged ? (
                        <span className="font-semibold text-amber-700">
                          same tax
                          <span className="block text-[11px] font-normal text-amber-700">
                            different payer
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="text-right">
                      {mayBook && (
                        <button className="btn-secondary btn-sm" disabled={busy}
                                onClick={() => {
                                  const first = determination.find((d) => d.wo_id === r.wo_id)
                                  if (!first) return
                                  setOverriding(first)
                                  setOverrideTo(r.override ?? 'FORCE_FORWARD')
                                  setOverrideWhy('')
                                }}>
                          Settle
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={6}>
                    What would change on these orders
                    {payerFlips.length > 0 && (
                      <span className="block text-[11px] font-normal text-amber-700">
                        and on {payerFlips.length} more the tax is the same but OVAL
                        becomes the one who pays it
                        {newLiability > 0 && ` — ${rupees(newLiability)} of liability that is his today`}
                      </span>
                    )}
                  </td>
                  <td className={`num ${wouldChange < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {rupees(wouldChange)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {onlyDifferences && agreeing > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            {agreeing === 1
              ? 'One more work order is not shown, because both rules give it the same answer '
              : `${agreeing} more work orders are not shown, because both rules give them the same answer `}
            — a registered contractor on a service that is not on the notified list is
            forward charge either way, and there is nothing to decide.{' '}
            <button className="font-semibold text-emerald-700 underline"
                    onClick={() => setOnlyDifferences(false)}>
              Show every order
            </button>
          </p>
        )}

        {isAdmin && (
          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold text-slate-800">
                  {byCategory
                    ? 'The notified list decides reverse charge.'
                    : 'The earlier rule decides reverse charge.'}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Moving this changes the tax on every bill raised from now on. Approved
                  bills never move. Admin only, and it goes on the record.
                </p>
              </div>
              <button className={byCategory ? 'btn-secondary' : 'btn-primary'}
                      disabled={busy} onClick={() => flipSwitch(!byCategory)}>
                {byCategory ? 'Go back to the earlier rule' : 'Use the notified list'}
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* 4 ------------------------------------------------------------------ */}
      {needDecision.length > 0 && (
        <Card title="Nobody has decided these"
              subtitle="The notification turns on whether the supplier is a body corporate, and the PAN cannot say. A partnership firm is reverse charge; an LLP is not.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr><th>Work order</th><th>Line</th><th>Supplier</th><th>Why</th><th /></tr>
              </thead>
              <tbody>
                {needDecision.map((d) => (
                  <tr key={d.wo_line_id}>
                    <td className="whitespace-nowrap font-medium">{d.wo_id}</td>
                    <td className="max-w-[180px] truncate text-xs">
                      {d.sl_no}. {d.line_description}
                      <span className="block text-[11px] text-slate-400">{d.sac_code}</span>
                    </td>
                    <td className="max-w-[150px] truncate">{d.vendor_name}</td>
                    <td className="max-w-[320px] text-[11px] text-slate-500">{d.reason}</td>
                    <td className="text-right">
                      {mayBook && (
                        <button className="btn-secondary btn-sm" disabled={busy}
                                onClick={() => { setOverriding(d); setOverrideTo('FORCE_FORWARD'); setOverrideWhy('') }}>
                          Settle
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ---- raise -------------------------------------------------------- */}
      <Modal open={!!raising} onClose={() => setRaising(null)}
             title="Raise a self-invoice"
             subtitle={raising ? `RA-${raising.ra_no} · ${raising.vendor_name}` : ''}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setRaising(null)}>Cancel</button>
                 <button className="btn-primary" disabled={busy} onClick={raiseSelfInvoice}>
                   Raise it
                 </button>
               </>
             }>
        {raising && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {rupees(raising.rcm_gst_amount)} of tax on a taxable value of{' '}
              {rupees(raising.gross_value)}. The document is raised under section 31(3)(f)
              because {raising.vendor_name} is not registered and cannot issue one.
            </p>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                Date the supply was received
              </span>
              <input type="date" className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                     value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
              <span className="mt-1 block text-[11px] text-slate-400">
                Defaults to the end of the period the bill covers, which is when the work
                was in fact received. The thirty days of rule 47A run from this date, not
                from the approval.
              </span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Remarks</span>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </label>
            {raising.overdue && (
              <Alert tone="warning" title="Already past the thirty days">
                It will be raised and flagged as late on its own face. The tax and the
                credit are unaffected; the delay is a record-keeping default.
              </Alert>
            )}
          </div>
        )}
      </Modal>

      {/* ---- settle ------------------------------------------------------- */}
      <Modal open={!!overriding} onClose={() => setOverriding(null)}
             title="Settle the tax treatment"
             subtitle={overriding ? `${overriding.wo_id} · ${overriding.vendor_name}` : ''}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setOverriding(null)}>Cancel</button>
                 <button className="btn-primary" disabled={busy || !overrideWhy.trim()}
                         onClick={saveOverride}>
                   Record it
                 </button>
               </>
             }>
        {overriding && (
          <div className="space-y-3">
            <p className="rounded bg-slate-50 p-2 text-xs text-slate-600">{overriding.reason}</p>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Treatment</span>
              <Select value={overrideTo} onChange={(e) => setOverrideTo(e.target.value)}
                      options={[
                        { value: '', label: 'Let the notified list decide' },
                        { value: 'FORCE_RCM', label: 'Reverse charge — OVAL pays the tax' },
                        { value: 'FORCE_FORWARD', label: 'Forward charge — he charges the tax' },
                        { value: 'FORCE_NO_TAX', label: 'No tax arises on this supply' },
                      ]} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                Why. Required, and it goes on the order's record.
              </span>
              <Textarea rows={3} value={overrideWhy}
                        onChange={(e) => setOverrideWhy(e.target.value)}
                        placeholder="e.g. Confirmed a partnership firm from the deed, so not a body corporate." />
            </label>
            <p className="text-[11px] text-slate-400">
              Refused on an order that already has an approved bill — raise the correction
              on the next bill instead.
            </p>
          </div>
        )}
      </Modal>

      <Confirm open={!!cancelling}
               title="Cancel this self-invoice"
               message={cancelling
                 ? `${cancelling.id} declares ${rupees(cancelling.total_tax)} of tax. Cancelling it removes that from the month's liability and puts the bill back on the clock.`
                 : ''}
               confirmLabel="Cancel it" tone="danger"
               requireReason reasonLabel="Why it is being cancelled"
               onCancel={() => setCancelling(null)}
               onConfirm={(reason) => cancelSelfInvoice(reason ?? '')} />

      <Confirm open={!!cancellingVoucher}
               title="Cancel this payment voucher"
               message={cancellingVoucher
                 ? `${cancellingVoucher.id} evidences a payment of ${rupees(cancellingVoucher.amount_paid)}. Cancelling it puts that payment back on the list above, waiting for a voucher.`
                 : ''}
               confirmLabel="Cancel it" tone="danger"
               requireReason reasonLabel="Why it is being cancelled"
               onCancel={() => setCancellingVoucher(null)}
               onConfirm={(reason) => cancelVoucher(reason ?? '')} />
    </div>
  )
}

export default ReverseCharge
